import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Button from '../components/common/Button'; 
import { useWallet } from '../contexts/WalletContext';
import { 
  ArrowRightIcon, 
  SparklesIcon, 
  CpuChipIcon, 
  RocketLaunchIcon, 
  GlobeAltIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon,
  CommandLineIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline'; 

const HomePage = () => {
  const { currentAccount } = useWallet();

  // Animation Variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  const planetVariants = {
    animate: {
      boxShadow: [
        "0px 10px 100px -20px rgba(168, 85, 247, 0.4)",
        "0px 10px 150px -20px rgba(168, 85, 247, 0.7)",
        "0px 10px 100px -20px rgba(168, 85, 247, 0.4)"
      ],
      transition: {
        duration: 5,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <div className="relative min-h-[calc(100vh-80px)] overflow-hidden bg-[#030014]">
      
      {/* --- Cosmic Background / Planet Effect --- */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
          
          {/* Static Stars / Noise */}
          <div className="absolute inset-0 opacity-20 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] mix-blend-overlay"></div>

          {/* PLANET HORIZON - Positioned to ensure visibility */}
          <div className="absolute top-[-350px] md:top-[-600px] left-1/2 -translate-x-1/2 w-[200vw] min-w-[1000px] h-[500px] md:h-[900px] z-0 pointer-events-none">
              <motion.div 
                 variants={planetVariants}
                 animate="animate"
                 className="w-full h-full rounded-[100%] bg-[#030014] border-b-[2px] border-purple-500/80 relative"
              >
                  {/* Inner Atmosphere Glow */}
                  <div className="absolute bottom-0 left-0 right-0 h-[200px] bg-gradient-to-t from-purple-600/30 via-indigo-600/10 to-transparent blur-2xl"></div>
                  
                  {/* Bright Horizon Core Line */}
                  <div className="absolute bottom-[-3px] left-1/2 -translate-x-1/2 w-[60%] h-[6px] bg-purple-400 blur-[4px] shadow-[0_0_20px_rgba(168,85,247,0.8)]"></div>
              </motion.div>
          </div>

          {/* Ambient Glows */}
          <div className="absolute top-[20%] left-[-10%] w-[400px] h-[400px] bg-blue-900/10 rounded-full blur-[100px] pointer-events-none mix-blend-screen" />
          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-purple-900/10 rounded-full blur-[120px] pointer-events-none mix-blend-screen" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 lg:py-20 relative z-10 w-full">
        
        {/* --- Hero Section --- */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-24"
        >
          
          {/* Left Column: Text Content */}
          <div className="text-center lg:text-left">
            <motion.div variants={itemVariants}>
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-slate-900/50 border border-slate-700/50 backdrop-blur-md mb-8 hover:border-slate-600 transition-colors cursor-default mx-auto lg:mx-0">
                <span className="flex h-2 w-2 relative mr-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                </span>
                <span className="text-slate-300 text-xs font-mono uppercase tracking-widest">
                  Lumina AI v1.0
                </span>
              </div>
            </motion.div>

            <motion.h1 variants={itemVariants} className="text-5xl md:text-7xl font-bold tracking-tight text-white mb-8 leading-[1.1]">
              Unlock the Future of <br className="hidden md:block"/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 animate-gradient-x">Decentralized Intelligence</span>
            </motion.h1>
            
            <motion.p variants={itemVariants} className="text-lg md:text-xl text-slate-400 mb-10 leading-relaxed max-w-xl mx-auto lg:mx-0 font-light">
              Deploy autonomous AI agents on the blockchain. Create, manage, and monetize decentralized intelligence with our next-generation marketplace.
            </motion.p>

            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 w-full sm:w-auto">
              {currentAccount ? (
                <>
                  <Link to="/create-agent" className="w-full sm:w-auto group">
                    <Button 
                      variant="primary" 
                      className="w-full sm:w-auto h-12 sm:h-14 px-6 sm:px-8 text-base sm:text-lg bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 shadow-lg shadow-blue-500/20 hover:shadow-blue-500/30 rounded-xl border-none flex items-center justify-center transition-all duration-300 transform active:scale-95"
                    >
                      <RocketLaunchIcon className="h-5 w-5 mr-2 group-hover:-translate-y-0.5 transition-transform" />
                      Launch Agent
                    </Button>
                  </Link>
                  <Link to="/my-agents" className="w-full sm:w-auto group">
                    <Button 
                      variant="secondary" 
                      className="w-full sm:w-auto h-12 sm:h-14 px-6 sm:px-8 text-base sm:text-lg bg-slate-800/50 hover:bg-slate-800 border border-slate-700 hover:border-slate-500 backdrop-blur-sm rounded-xl text-slate-300 group-hover:text-white flex items-center justify-center transition-all duration-300"
                    >
                      Console
                      <ArrowRightIcon className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </>
              ) : (
                <div className="p-1 rounded-xl bg-gradient-to-r from-slate-800 to-slate-900 p-[1px]">
                  <div className="px-6 py-4 rounded-xl bg-slate-950/90 border border-slate-800/50 text-slate-400 text-sm backdrop-blur-sm flex items-center gap-3">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                      Please connect your wallet to access the platform
                  </div>
                </div>
              )}
            </motion.div>

            <motion.div variants={itemVariants} className="mt-8 flex items-center justify-center lg:justify-start">
               <div className="group relative inline-flex items-center gap-3 px-5 py-3 rounded-xl bg-slate-900/50 border border-slate-700/50 backdrop-blur-md hover:border-blue-500/50 transition-colors">
                  <span className="text-slate-400 text-sm font-medium">CA:</span>
                  <code className="text-blue-400 text-sm font-mono tracking-wide">0x00000000000000000000000000000000000</code>
                  <button 
                    onClick={() => navigator.clipboard.writeText("0x00000000000000000000000000000000000")}
                    className="p-1.5 hover:bg-slate-800 rounded-lg text-slate-500 hover:text-white transition-colors cursor-pointer"
                    title="Copy Address"
                  >
                    <DocumentDuplicateIcon className="w-4 h-4" />
                  </button>
               </div>
            </motion.div>
          </div>

          {/* Right Column: Terminal Visualization */}
          <motion.div 
            variants={itemVariants}
            className="hidden lg:block relative group perspective-1000"
          >
             {/* Glow Effect */}
             <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl blur-2xl opacity-20 group-hover:opacity-40 transition duration-1000"></div>
             
             {/* Terminal Window */}
             <div className="relative rounded-xl bg-[#0D1117] border border-slate-800 shadow-2xl overflow-hidden font-mono text-xs leading-relaxed transform transition-all duration-500 group-hover:scale-[1.02]">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 bg-slate-900/80 border-b border-slate-800/50 backdrop-blur-md">
                    <div className="flex space-x-2">
                        <div className="w-3 h-3 rounded-full bg-red-500/20 border border-red-500/50"></div>
                        <div className="w-3 h-3 rounded-full bg-yellow-500/20 border border-yellow-500/50"></div>
                        <div className="w-3 h-3 rounded-full bg-green-500/20 border border-green-500/50"></div>
                    </div>
                    <div className="text-slate-500 text-[10px] uppercase tracking-wider">luminaai_agent_core</div>
                </div>

                {/* Body */}
                <div className="p-6 space-y-4 text-slate-400 min-h-[360px] flex flex-col">
                     <div className="flex">
                         <span className="text-purple-400 mr-2">import</span>
                         <span className="text-white">luminaai_sdk</span>
                         <span className="text-slate-600 mx-2">as</span>
                         <span className="text-yellow-300">nw</span>
                     </div>
                     
                     <div className="space-y-1">
                         <div className="text-slate-500 italic">// Initialize Neural Link</div>
                         <div className="flex">
                             <span className="text-blue-400">const</span>
                             <span className="text-white ml-2">agent</span>
                             <span className="text-slate-500 mx-2">=</span>
                             <span className="text-purple-400">await</span>
                             <span className="text-yellow-300 ml-2">nw.deploy</span>
                             <span className="text-slate-500">(</span>
                             <span className="text-orange-400">"v2.1"</span>
                             <span className="text-slate-500">);</span>
                         </div>
                     </div>

                     <div className="pl-4 border-l border-slate-800 space-y-2 py-2 my-2 bg-slate-900/30 rounded-r-lg">
                         <div className="flex items-center gap-2">
                             <span className="w-2 h-2 rounded-full bg-green-500"></span>
                             <span className="text-slate-300">Connecting to localized grid...</span>
                             <span className="text-green-500">[OK]</span>
                         </div>
                         <div className="flex items-center gap-2">
                             <span className="w-2 h-2 rounded-full bg-green-500"></span>
                             <span className="text-slate-300">Syncing learning models...</span>
                             <span className="text-green-500">[OK]</span>
                         </div>
                         <div className="flex items-center gap-2">
                             <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></span>
                             <span className="text-slate-300">Allocating GPU resources...</span>
                             <span className="text-blue-400">[PENDING]</span>
                         </div>
                     </div>

                     <div className="flex-1"></div>

                     <div className="pt-4 border-t border-slate-800">
                        <div className="text-emerald-400 mb-1">➜ System Output:</div>
                        <div className="text-slate-300 typing-effect">
                           Agent "CyberOracle" is online and awaiting prompt..._
                        </div>
                     </div>
                </div>
             </div>
          </motion.div>
        </motion.div>

        {/* --- Live Network Stats --- */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-8 py-10 border-y border-slate-800/50 bg-slate-900/20 backdrop-blur-md mb-32"
        >
            <div className="text-center group cursor-default">
                <div className="text-2xl md:text-3xl font-bold text-white mb-1 group-hover:text-blue-400 transition-colors">10k+</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold">Daily Inferences</div>
            </div>
             <div className="text-center group cursor-default">
                <div className="text-2xl md:text-3xl font-bold text-white mb-1 group-hover:text-purple-400 transition-colors">500+</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold">Active Nodes</div>
            </div>
             <div className="text-center group cursor-default">
                <div className="text-2xl md:text-3xl font-bold text-white mb-1 group-hover:text-emerald-400 transition-colors">$2.4M</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold">TVL</div>
            </div>
             <div className="text-center group cursor-default">
                <div className="text-2xl md:text-3xl font-bold text-white mb-1 group-hover:text-pink-400 transition-colors">99.9%</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold">Uptime</div>
            </div>
         </motion.div>

        {/* --- Info Project / About --- */}
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="mb-32 relative"
        >
             <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-[80px] -z-10"></div>
             <div className="text-center max-w-3xl mx-auto mb-16">
                 <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">The Operating System for <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">Digital Intelligence</span></h2>
                 <p className="text-slate-400 text-lg leading-relaxed">
                    Lumina AI solves the core problems of centralized AI: censorship, data silos, and lack of monetization for creators. By combining cryptographic proofs with neural networks, we are building a permissionless grid where anyone can deploy, rent, or scale AI agents.
                 </p>
            </div>
        </motion.div>

        {/* --- Features Grid --- */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-32"
        >
          {/* Feature 1 */}
          <motion.div variants={itemVariants} className="group relative p-8 bg-slate-900/40 backdrop-blur-xl border border-slate-800/50 rounded-3xl hover:bg-slate-800/60 transition-all duration-300 hover:border-slate-700">
            <div className="w-12 h-12 bg-blue-500/10 rounded-2xl flex items-center justify-center mb-6 border border-blue-500/20 group-hover:border-blue-500/40">
              <CpuChipIcon className="h-6 w-6 text-blue-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Neural Processing</h3>
            <p className="text-slate-400 text-sm leading-relaxed">Access high-performance LLMs optimized for decentralized execution.</p>
          </motion.div>

          {/* Feature 2 */}
          <motion.div variants={itemVariants} className="group relative p-8 bg-gradient-to-b from-slate-800/40 to-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-3xl md:col-span-1 shadow-2xl shadow-purple-900/5">
             <div className="w-12 h-12 bg-purple-500/10 rounded-2xl flex items-center justify-center mb-6 border border-purple-500/20 group-hover:border-purple-500/40">
              <SparklesIcon className="h-6 w-6 text-purple-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Autonomous Agents</h3>
            <p className="text-slate-400 text-sm leading-relaxed">Agents that can think, act, and interact independently.</p>
          </motion.div>

           {/* Feature 3 */}
           <motion.div variants={itemVariants} className="group relative p-8 bg-slate-900/40 backdrop-blur-xl border border-slate-800/50 rounded-3xl hover:bg-slate-800/60 transition-all duration-300 hover:border-slate-700">
            <div className="w-12 h-12 bg-pink-500/10 rounded-2xl flex items-center justify-center mb-6 border border-pink-500/20 group-hover:border-pink-500/40">
              <GlobeAltIcon className="h-6 w-6 text-pink-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Global Registry</h3>
            <p className="text-slate-400 text-sm leading-relaxed">Publish to the global registry and earn rewards instantly.</p>
          </motion.div>
          
           {/* Feature 4 */}
           <motion.div variants={itemVariants} className="group relative p-8 bg-slate-900/40 backdrop-blur-xl border border-slate-800/50 rounded-3xl hover:bg-slate-800/60 transition-all duration-300 hover:border-slate-700">
              <div className="w-12 h-12 bg-yellow-500/10 rounded-2xl flex items-center justify-center mb-6 border border-yellow-500/20 group-hover:border-yellow-500/40">
                <CurrencyDollarIcon className="h-6 w-6 text-yellow-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Defi Integration</h3>
              <p className="text-slate-400 text-sm leading-relaxed">Native token payments, staking rewards, and automated revenue splits.</p>
           </motion.div>

           {/* Feature 5 */}
           <motion.div variants={itemVariants} className="group relative p-8 bg-slate-900/40 backdrop-blur-xl border border-slate-800/50 rounded-3xl hover:bg-slate-800/60 transition-all duration-300 hover:border-slate-700">
              <div className="w-12 h-12 bg-green-500/10 rounded-2xl flex items-center justify-center mb-6 border border-green-500/20 group-hover:border-green-500/40">
                <ShieldCheckIcon className="h-6 w-6 text-green-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Verifiable Proofs</h3>
              <p className="text-slate-400 text-sm leading-relaxed">Cryptographic proof of inference ensures models run exactly as specified.</p>
           </motion.div>

           {/* Feature 6 */}
           <motion.div variants={itemVariants} className="group relative p-8 bg-slate-900/40 backdrop-blur-xl border border-slate-800/50 rounded-3xl hover:bg-slate-800/60 transition-all duration-300 hover:border-slate-700">
              <div className="w-12 h-12 bg-red-500/10 rounded-2xl flex items-center justify-center mb-6 border border-red-500/20 group-hover:border-red-500/40">
                <CommandLineIcon className="h-6 w-6 text-red-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Dev First SDK</h3>
              <p className="text-slate-400 text-sm leading-relaxed">TypeScript SDK to deploy agents with just a few lines of code.</p>
           </motion.div>
        </motion.div>

        {/* --- Comparison Table --- */}
        <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-32"
        >
             <div className="text-center mb-16">
                 <h2 className="text-3xl font-bold text-white mb-4">Why Lumina AI?</h2>
                 <p className="text-slate-400">The shift from centralized to decentralized intelligence</p>
             </div>

             <div className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/50 backdrop-blur-sm">
                 <div className="grid grid-cols-3 bg-slate-900/80 p-6 border-b border-slate-800">
                     <div className="col-span-1 text-slate-500 font-semibold uppercase tracking-wider text-xs">Comparison</div>
                     <div className="col-span-1 text-center text-slate-400 font-semibold uppercase tracking-wider text-xs">Traditional Web2 AI</div>
                     <div className="col-span-1 text-center text-emerald-400 font-bold uppercase tracking-wider text-xs flex items-center justify-center gap-2">
                         <SparklesIcon className="w-4 h-4"/> luminaai
                     </div>
                 </div>
                 
                 {/* Rows */}
                 {[
                    { label: "Ownership", web2: "Corporate Owned", web3: "User Owned" },
                    { label: "Privacy", web2: "Data Harvested", web3: "Private & Encrypted" },
                    { label: "Censorship", web2: "Heavy Guardrails", web3: "Permissionless" },
                    { label: "Revenue", web2: "100% to Platform", web3: "Shared with Creators" },
                 ].map((row, index) => (
                    <div key={index} className="grid grid-cols-3 p-6 border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
                        <div className="col-span-1 text-slate-300 font-medium">{row.label}</div>
                        <div className="col-span-1 text-center text-slate-500">{row.web2}</div>
                        <div className="col-span-1 text-center text-white">{row.web3}</div>
                    </div>
                 ))}
             </div>
        </motion.div>

        {/* --- Footer --- */}
        <footer className="border-t border-slate-800/50 pt-16 pb-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12 text-sm">
                <div>
                    <h4 className="text-white font-bold mb-4">Product</h4>
                    <ul className="space-y-2 text-slate-400">
                        <li><a href="#" className="hover:text-blue-400 transition-colors">Marketplace</a></li>
                        <li><a href="#" className="hover:text-blue-400 transition-colors">LuminaAI SDK</a></li>
                        <li><a href="#" className="hover:text-blue-400 transition-colors">Agent Studio</a></li>
                    </ul>
                </div>
                <div>
                     <h4 className="text-white font-bold mb-4">Resources</h4>
                    <ul className="space-y-2 text-slate-400">
                        <li><a href="#" className="hover:text-blue-400 transition-colors">Documentation</a></li>
                        <li><a href="#" className="hover:text-blue-400 transition-colors">Whitepaper</a></li>
                        <li><a href="#" className="hover:text-blue-400 transition-colors">API Reference</a></li>
                    </ul>
                </div>
                 <div>
                     <h4 className="text-white font-bold mb-4">Community</h4>
                    <ul className="space-y-2 text-slate-400">
                        <li><a href="#" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors">Telegram</a></li>
                        <li><a href="https://x.com/Luminabase" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors">Twitter / X</a></li>
                        <li><a href="#" className="hover:text-blue-400 transition-colors">GitHub</a></li>
                    </ul>
                </div>
                 <div className="col-span-2 md:col-span-1">
                    <div className="flex items-center gap-2 mb-4">
                        <div className="h-6 w-6 rounded-full bg-gradient-to-tr from-blue-500 to-purple-500"></div>
                        <span className="text-white font-bold">Lumina AI</span>
                    </div>
                    <p className="text-slate-500 text-xs">
                        Building the decentralized intelligence grid.
                        <br/>&copy; 2026 Lumina AI.
                    </p>
                </div>
            </div>
        </footer>

      </div>
    </div>
  );
};

export default HomePage;